import { openPopup } from "@/helper/DxHelper";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { useAppSelector } from "@/hooks/useAppSelector";
import { NPVHeader } from "@/redux/npv/npv-slice";
import { CalculateGrid } from "@/redux/uq/uq-actions";
import { UPHeader, uqActions } from "@/redux/uq/uq-slice";
import { ContextMenu, Tooltip } from "devextreme-react";
import { Position } from "devextreme-react/cjs/autocomplete";
import { PopupRef } from "devextreme-react/cjs/popup";

type Props = {
  headerName: string;
  header?: UPHeader | NPVHeader;
  colaOverridePopupRef?: React.RefObject<PopupRef>;
  titleRef?: React.RefObject<PopupRef>;
  isNpv?: boolean;
};

function GridHeader({ headerName, header, colaOverridePopupRef, titleRef, isNpv }: Props) {
  const dispatch = useAppDispatch();
  const { settings, gridSet, headerTitles: upHeaderTitles } = useAppSelector((state) => state.uq);
  const { headerTitles: npvHeaderTitles } = useAppSelector((state) => state.npv);

  const isIncomeOrSocialSecurity =
    header?.includes("Income") || (headerName.includes("Security") && header !== "SocialSecurity");
  const isIncome1 = header === "Income1";
  const isIncome2 = header === "Income2";
  const isSocialSecurity1 = header === "SocialSecurity1";
  const isSocialSecurity2 = header === "SocialSecurity2";

  const currentTitle = header ? (isNpv ? npvHeaderTitles[header] : upHeaderTitles[header]) : null;

  return (
    <>
      <div
        className={`group relative h-full p-4 print:px-[2px] ${
          header ? "cursor-pointer" : ""
        } flex h-full w-full flex-col text-center`}
        id={`${header}`}
        onClick={() => {
          if (header && !isNpv && titleRef) {
            dispatch(uqActions.setHeader(header as UPHeader));
            openPopup(titleRef);
          }
        }}
      >
        {currentTitle ? (
          <div className="mb-3 whitespace-pre-line break-words font-normal italic print:px-1 print:pb-1 print:!text-[12px] print:leading-tight">
            {currentTitle}
          </div>
        ) : null}

        <div
          className={`relative flex w-full flex-col items-center justify-center text-sm print:px-1 print:pb-1 print:!text-[12px] print:leading-tight`}
        >
          <span className={`max-w-full break-words uppercase`}>{headerName}</span>
        </div>

        {(settings?.colaIncome && isIncome1) ||
        (settings?.colaIncome2 && isIncome2) ||
        (settings?.colaSS1 && isSocialSecurity1) ||
        (settings?.colaSS2 && isSocialSecurity2) ? (
          <>
            <img
              className="absolute right-0 top-0 print:h-2 print:w-2"
              src="/icons/break-cell.svg"
            />

            <Tooltip showEvent="mouseenter" hideEvent="mouseleave" target={`#${header}`}>
              <Position at={"top"} my={"bottom"} offset={{ x: 0, y: 0 }} />
              <span className="text-base">
                COLA Override:{` `}
                {settings?.colaIncome && isIncome1
                  ? `${Number(settings.colaIncome * 100).toFixed(2)}%`
                  : null}
                {settings?.colaIncome2 && isIncome2
                  ? `${Number(settings.colaIncome2 * 100).toFixed(2)}%`
                  : null}
                {settings?.colaSS1 && isSocialSecurity1
                  ? `${Number(settings.colaSS1 * 100).toFixed(2)}%`
                  : null}
                {settings?.colaSS2 && isSocialSecurity2
                  ? `${Number(settings.colaSS2 * 100).toFixed(2)}%`
                  : null}
              </span>
            </Tooltip>
          </>
        ) : null}
      </div>

      {isIncomeOrSocialSecurity && !isNpv ? (
        <ContextMenu
          target={`#${header}`}
          items={[
            { text: "COLA Override", icon: "edit" },
            {
              text: "Reset",
              icon: "revert",
              visible: Boolean(
                (settings?.colaIncome && isIncome1) ||
                  (settings?.colaIncome2 && isIncome2) ||
                  (settings?.colaSS1 && isSocialSecurity1) ||
                  (settings?.colaSS2 && isSocialSecurity2)
              ),
            },
          ]}
          onItemClick={(e) => {
            switch (e.itemData?.text?.toLowerCase()) {
              case "cola override":
                dispatch(uqActions.setUpdateHeader(header as UPHeader));
                setTimeout(() => {
                  if (colaOverridePopupRef) openPopup(colaOverridePopupRef);
                }, 300);
                break;
              case "reset":
                if (isIncome1) {
                  dispatch(uqActions.setSettings({ ...settings, colaIncome: null }));
                  dispatch(CalculateGrid(0, gridSet));
                } else if (isIncome2) {
                  dispatch(uqActions.setSettings({ ...settings, colaIncome2: null }));
                  dispatch(CalculateGrid(0, gridSet));
                } else if (isSocialSecurity1) {
                  dispatch(uqActions.setSettings({ ...settings, colaSS1: null }));
                  dispatch(CalculateGrid(0, gridSet));
                } else {
                  dispatch(uqActions.setSettings({ ...settings, colaSS2: null }));
                  dispatch(CalculateGrid(0, gridSet));
                }
            }
          }}
        />
      ) : null}
    </>
  );
}

export default GridHeader;
